import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Statistic, Tag } from 'antd';
import { Terminal } from 'lucide-react';
import { useState } from 'react';
import { AxiomLogDetailsDrawer } from './AxiomLogDetailsDrawer';
import { AxiomLogFilters } from './AxiomLogFilters';
import { useAxiomLogColumns } from './columns';

export const Route = createFileRoute('/admin-console/monitoring/axiom-logs')({
    staticData: {
        permissions: ['admin_system:read'],
        breadcrumb: {
            title: 'Axiom logs',
        },
        ui: {
            icon: Terminal,
            title: 'Axiom logs',
            subtitle: 'View and analyse application logs from Axiom',
            menuDescription: 'Query application logs stored in Axiom',
        },
    },
    component: AxiomLogsPage,
});

type AxiomLog = RouterOutputs['admin']['axiomLog']['queryAxiomLogs']['logs'][number];
type QueryVariables = RouterInputs['admin']['axiomLog']['queryAxiomLogs'];

function AxiomLogsPage() {
    const { trpc } = useTrpc();

    const [queryVariables, setQueryVariables] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
    });
    const [selectedLog, setSelectedLog] = useState<AxiomLog | null>(null);

    const { data, error, isFetching, refetch } = trpc.admin.axiomLog.queryAxiomLogs.useQuery(queryVariables);

    const columns = useAxiomLogColumns(setSelectedLog, data?.userLookup, data?.orgLookup);

    const tableProps = usePrismaTable({
        data: data?.logs || [],
        total: data?.logs.length || 0,
        loading: isFetching,
        columns,
        rowKey: (record: AxiomLog) => `${record._time || ''}-${record.requestId || Math.random()}`,
        queryParams: queryVariables,
        onParamsChange: setQueryVariables,
        pagination: {
            position: 'both',
            showSizeChanger: true,
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'axiom-logs-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
    });

    const handleFiltersChange = (filters: Partial<QueryVariables>) => {
        // `service` is explicitly undefined when Source is cleared so it can remove a previous
        // selection. Treat that all-empty payload the same as the Clear button.
        if (Object.values(filters).every((value) => value === undefined)) {
            setQueryVariables({ limit: DEFAULT_PAGE_SIZE });
        } else {
            setQueryVariables((prev) => ({
                ...prev,
                ...filters,
            }));
        }
    };

    if (error) {
        errorMessage.show(error);
    }

    return (
        <PageController route={Route}>
            {/* Summary Statistics */}
            {data?.summary && (
                <div className="mb-4 grid grid-cols-4 gap-4">
                    <Card size="small">
                        <Statistic title="Total logs" value={data.summary.total} />
                    </Card>
                    <Card size="small">
                        <Statistic
                            title="Errors"
                            value={data.summary.errorCount}
                            valueStyle={{
                                color: data.summary.errorCount > 0 ? 'var(--color-status-danger)' : undefined,
                            }}
                        />
                    </Card>
                    <Card size="small">
                        <Statistic
                            title="Warnings"
                            value={data.summary.warnCount}
                            valueStyle={{
                                color: data.summary.warnCount > 0 ? 'var(--color-status-warning)' : undefined,
                            }}
                        />
                    </Card>
                    <Card size="small">
                        <Statistic
                            title="Time range"
                            formatter={() => (
                                <div className="flex flex-wrap items-center gap-1 text-xs">
                                    <Tag color="blue" variant="outlined">
                                        {data.summary.queryTimeRange.start?.slice(0, 19)}
                                    </Tag>
                                    <span>to</span>
                                    <Tag color="blue" variant="outlined">
                                        {data.summary.queryTimeRange.end?.slice(0, 19)}
                                    </Tag>
                                </div>
                            )}
                        />
                    </Card>
                </div>
            )}

            <TableToolbar filters={<AxiomLogFilters onChange={handleFiltersChange} />} variant="enhanced" />

            <PrismaTable {...tableProps} size="small" scroll={{ x: 'max-content' }} />

            <AxiomLogDetailsDrawer
                log={selectedLog}
                onClose={() => setSelectedLog(null)}
                userLookup={data?.userLookup}
                orgLookup={data?.orgLookup}
            />
        </PageController>
    );
}
